// =====================================================
// 📁 POSAddProductToSaleModal.tsx
// =====================================================

import { useState } from 'react';

import InputField from '../../../Forms/Input/InputField';

import FormModal from '../../../Modals/FormModal';

import { useCurrencyInput } from '../../../../hooks/useCurrencyInput';

import { ProductPanelItem } from '../../../../types/productpanel/ProductPanelItem';

import { TaxType } from '../../../../types/sale/TaxType';
import SelectField from '../../../Forms/Input/SelectField';

interface Props {
  isOpen: boolean;

  product: ProductPanelItem;

  saleId: number;

  onClose: () => void;

  onConfirm: (payload: {
    saleId: number;

    productId: number;

    quantity: number;

    unitPrice: number;

    discountPercentage: number;

    taxType: TaxType;
  }) => void;
}

const POSAddProductToSaleModal: React.FC<Props> = ({
  isOpen,

  product,

  saleId,

  onClose,

  onConfirm,
}) => {
  // =====================================================
  // 🔹 State
  // =====================================================
  const [quantity, setQuantity] = useState<string>('1');

  const [unitPrice, setUnitPrice] = useState<string>(product.price.toString());

  const [taxType, setTaxType] = useState<TaxType>('TAXED');

  const [discountPercentage, setDiscountPercentage] = useState<string>('0');

  const discountDisabled = product.appliesDiscount === false;

  // 💵 Precio unitario — formato legible (6789 -> 6,789.00)
  const unitPriceInput = useCurrencyInput(unitPrice, (value, isEmpty) => {
    setUnitPrice(isEmpty ? '' : String(value));
  });

  // =====================================================
  // 🔹 Handlers
  // =====================================================
  const handleConfirm = () => {
    const parsedQuantity = Number(quantity);

    const parsedPrice = Number(unitPrice);

    const parsedDiscountPercentage = Number(discountPercentage);

    // =====================================================
    // 🔹 Validation
    // =====================================================
    if (
      !quantity ||
      !unitPrice ||
      parsedQuantity <= 0 ||
      parsedPrice < 0 ||
      parsedDiscountPercentage < 0 ||
      parsedDiscountPercentage > 100
    ) {
      alert('Por favor, ingresa valores válidos.');

      return;
    }

    // =====================================================
    // 🔹 Submit
    // =====================================================
    onConfirm({
      saleId,

      productId: product.id,

      quantity: parsedQuantity,

      unitPrice: parsedPrice,

      discountPercentage: parsedDiscountPercentage,

      taxType,
    });
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Agregar producto"
      maxWidth="max-w-2xl"
      overlayBg="black"
    >
      {/* ===================================================== */}
      {/* 🔹 Product Info */}
      {/* ===================================================== */}
      <div>
        <h3
          className="
              mb-5
              text-sm
              font-semibold
              text-black

              dark:text-white
            "
        >
          {product.name}
        </h3>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Fields */}
      {/* ===================================================== */}
      <div
        className="
        grid
        grid-cols-1
        gap-4

        md:grid-cols-2
      "
      >
        <SelectField
          label="Tipo Tributario"
          name="taxType"
          value={taxType}
          onChange={(e) => setTaxType(e.target.value as TaxType)}
          disabled
          options={[
            {
              value: 'TAXED',
              label: 'Gravado',
            },
            {
              value: 'EXEMPT',
              label: 'Exento',
            },
            {
              value: 'NON_SUBJECT',
              label: 'No sujeto',
            },
          ]}
          iconKey="document"
        />

        <InputField
          label="Descuento (%)"
          name="discountPercentage"
          type="number"
          step="0.01"
          min={0}
          max={100}
          disabled={discountDisabled}
          value={discountDisabled ? '' : discountPercentage}
          onChange={(e) => setDiscountPercentage(e.target.value)}
          iconKey="discount"
        />

        <InputField
          label="Cantidad"
          name="quantity"
          type="number"
          min={1}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          iconKey="product"
        />

        <InputField
          label="Precio Unitario"
          name="unitPrice"
          type="text"
          inputMode="decimal"
          value={unitPriceInput.displayValue}
          onFocus={unitPriceInput.onFocus}
          onBlur={unitPriceInput.onBlur}
          onKeyDown={unitPriceInput.onKeyDown}
          onChange={(e) => unitPriceInput.onChangeText(e.target.value)}
          iconKey="dollar"
        />
      </div>

      {discountDisabled && (
        <p className="mt-2 text-xs text-bodydark2">
          Este producto no admite descuentos.
        </p>
      )}

      {/* ===================================================== */}
      {/* 🔹 Actions */}
      {/* ===================================================== */}
      <div className="mt-8 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="
          inline-flex
          rounded

          bg-[#DC3545]

          px-4
          py-2

          text-sm
          font-medium
          text-white

          shadow-sm

          transition

          hover:brightness-110
        "
        >
          Cancelar
        </button>

        <button
          onClick={handleConfirm}
          className="
          inline-flex
          rounded

          bg-primary

          px-4
          py-2

          text-sm
          font-medium
          text-white

          shadow-sm

          transition

          hover:brightness-110
        "
        >
          Agregar
        </button>
      </div>
    </FormModal>
  );
};

export default POSAddProductToSaleModal;
